import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import { eq, or } from 'ember-truth-helpers';
import { RECURRING_OPTIONS } from 'garaje/utils/enums';
import type { ValueOf } from 'type-fest';

export type RecurringOptions = {
  ALL: (typeof RECURRING_OPTIONS)['ALL'] | undefined;
  THIS: (typeof RECURRING_OPTIONS)['THIS'] | undefined;
  THIS_AND_FOLLOWING: (typeof RECURRING_OPTIONS)['THIS_AND_FOLLOWING'];
};

interface SelectRecurringInvitesSignature {
  Args: {
    disabled?: RecurringOptions;
    modelName: string;
    onChange: (option: ValueOf<typeof RECURRING_OPTIONS>) => void;
    onSubmitTask?: Task<void, [e: Event]>;
    options?: RecurringOptions;
    scope?: string;
  };
}

export default class SelectRecurringInvites extends Component<SelectRecurringInvitesSignature> {
  @tracked selectedOption = this.args.scope || RECURRING_OPTIONS.THIS;

  get options(): RecurringOptions {
    return this.args.options || RECURRING_OPTIONS;
  }

  @action
  onChangeOption(evt: Event): void {
    const option = <ValueOf<typeof RECURRING_OPTIONS>>(<HTMLInputElement>evt.target).value;

    this.selectedOption = option;
    this.args.onChange(option);
  }

  <template>
    <div class="pt-4">
      {{#if this.options.THIS}}
        {{#let (or @disabled.THIS @onSubmitTask.isRunning) as |disabled|}}
          <p class="my-2">
            <label class="label-reset {{if disabled 'text-grey cursor-not-allowed'}}">
              <input
                type="radio"
                name="modify-which-invites"
                value={{this.options.THIS}}
                checked={{eq this.selectedOption this.options.THIS}}
                disabled={{disabled}}
                {{on "change" this.onChangeOption}}
                data-test-this-invite
              />
              This
              {{@modelName}}
            </label>
          </p>
        {{/let}}
      {{/if}}
      {{#if this.options.THIS_AND_FOLLOWING}}
        {{#let (or @disabled.THIS_AND_FOLLOWING @onSubmitTask.isRunning) as |disabled|}}
          <p class="my-2">
            <label class="label-reset {{if disabled 'text-grey cursor-not-allowed'}}">
              <input
                type="radio"
                name="modify-which-invites"
                value={{this.options.THIS_AND_FOLLOWING}}
                checked={{eq this.selectedOption this.options.THIS_AND_FOLLOWING}}
                disabled={{disabled}}
                {{on "change" this.onChangeOption}}
                data-test-this-and-following-invites
              />
              This and following
              {{pluralize @modelName}}
            </label>
          </p>
        {{/let}}
      {{/if}}
      {{#if this.options.ALL}}
        {{#let (or @disabled.ALL @onSubmitTask.isRunning) as |disabled|}}
          <p class="my-2">
            <label class="label-reset {{if disabled 'text-grey cursor-not-allowed'}}">
              <input
                type="radio"
                name="modify-which-invites"
                value={{this.options.ALL}}
                checked={{eq this.selectedOption this.options.ALL}}
                disabled={{disabled}}
                {{on "change" this.onChangeOption}}
                data-test-all-invites
              />
              All
              {{pluralize @modelName}}
            </label>
          </p>
        {{/let}}
      {{/if}}
    </div>
  </template>
}
